import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Lock, Star } from 'lucide-react';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { getPageBackgroundGradient } from '../config/theme';
import { INSIGHT_EMOTIONS } from '../config/insightEmotions';
import { createPageUrl } from '../utils';

type LockedInsightLocationState = {
  emotionId: string;
};

function isValidLockedInsightState(s: unknown): s is LockedInsightLocationState {
  if (!s || typeof s !== 'object') return false;
  const id = (s as Record<string, unknown>).emotionId;
  if (typeof id !== 'string' || !id.trim()) return false;
  return INSIGHT_EMOTIONS.some((e) => e.id === id);
}

function translatedInsightEmotionLabel(
  t: (key: string, params?: Record<string, string>) => string,
  emotionId: string,
): string {
  const key = `library.knowYourself.emotion.${emotionId}`;
  const label = t(key);
  if (label !== key) return label;
  return emotionId
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const TESTIMONIAL_KEYS = ['a', 'c', 'd', 'e', 'f'] as const;

function TestimonialCard({
  tKey,
  t,
  isDark,
}: {
  tKey: string;
  t: (key: string, params?: Record<string, string>) => string;
  isDark: boolean;
}) {
  const quoteKey = `library.knowYourself.lockedInsight.testimonial.${tKey}`;
  const authorKey = `${quoteKey}Author`;
  const author = t(authorKey);
  const hasAuthor = author !== authorKey;

  return (
    <div
      className={`w-[min(260px,78vw)] flex-shrink-0 rounded-2xl p-4 ${
        isDark ? 'bg-[#0a2d6b]/90' : 'bg-blue-50/95 shadow-sm shadow-black/5'
      }`}
    >
      <div className="mb-3 flex justify-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className="h-4 w-4 fill-amber-400 text-amber-400"
            strokeWidth={0}
            aria-hidden
          />
        ))}
      </div>
      <p
        className={`text-center text-sm font-medium leading-relaxed ${isDark ? 'text-white/95' : 'text-gray-800'}`}
      >
        {t(quoteKey)}
      </p>
      {hasAuthor ? (
        <p
          className={`mt-3 text-center text-xs font-medium ${
            isDark ? 'text-white/55' : 'text-gray-500'
          }`}
        >
          — {author}
        </p>
      ) : null}
    </div>
  );
}

function TestimonialMarquee({
  t,
  isDark,
}: {
  t: (key: string, params?: Record<string, string>) => string;
  isDark: boolean;
}) {
  const loopKeys = [...TESTIMONIAL_KEYS, ...TESTIMONIAL_KEYS];
  return (
    <div className="relative -mx-5 overflow-hidden border-0 px-5 outline-none ring-0">
      <div className="insight-testimonial-marquee-track flex w-max min-w-0 gap-3 py-1">
        {loopKeys.map((key, idx) => (
          <TestimonialCard key={`${key}-${idx}`} tKey={key} t={t} isDark={isDark} />
        ))}
      </div>
    </div>
  );
}

const LockedInsightDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, theme } = useAppSettings();
  const isDark = theme === 'dark';

  const state = isValidLockedInsightState(location.state) ? location.state : null;

  useEffect(() => {
    if (!state) {
      navigate('/library', { replace: true });
    }
  }, [state, navigate]);

  if (!state) return null;

  const emotionLabel = translatedInsightEmotionLabel(t, state.emotionId);
  const headerTitle = t('library.knowYourself.lockedInsight.pageTitle', { emotion: emotionLabel });

  const whisperPath = createPageUrl('WhisperTalk');

  return (
    <div className="fixed inset-0 flex h-full w-full flex-col overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <header
          className="relative flex flex-shrink-0 items-center justify-center px-4 pb-3"
          style={{ paddingTop: 'max(7vh, env(safe-area-inset-top, 0px))' }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`absolute left-2 rounded-xl p-2 transition-colors ${
              isDark ? 'text-white hover:bg-white/10' : 'text-gray-800 hover:bg-black/5'
            }`}
            aria-label={t('common.back')}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h1
            className={`mx-12 text-center text-base font-medium leading-snug ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            {headerTitle}
          </h1>
        </header>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 pb-10">
          <div
            className={`mx-auto mt-2 max-w-sm rounded-[28px] border bg-gradient-to-b p-8 text-center shadow-lg ${
              isDark
                ? 'border-white/20 from-sky-500/35 via-blue-600/40 to-[#0a2d6b]/90'
                : 'border-blue-200/60 from-sky-200/90 via-blue-100/90 to-white/95'
            }`}
          >
            <div
              className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full ${
                isDark ? 'bg-white/15' : 'bg-white/80 shadow-sm'
              }`}
            >
              <Lock
                className={`h-7 w-7 ${isDark ? 'text-white' : 'text-blue-700'}`}
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <p
              className={`text-xs font-medium uppercase tracking-wide ${
                isDark ? 'text-white/75' : 'text-gray-600'
              }`}
            >
              {t('library.knowYourself.insights.whatMakesMe')}
            </p>
            <p className={`mt-2 text-2xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {emotionLabel}
            </p>
            <p
              className={`mx-auto mt-4 max-w-[240px] text-sm leading-relaxed ${
                isDark ? 'text-white/80' : 'text-gray-600'
              }`}
            >
              {t('library.knowYourself.lockedInsight.lumiUnderstanding')}
            </p>
            <button
              type="button"
              onClick={() => navigate(whisperPath)}
              className={`mt-8 w-full rounded-full border px-6 py-3.5 text-sm font-semibold transition-colors ${
                isDark
                  ? 'border-white/40 bg-white/10 text-white hover:bg-white/15'
                  : 'border-blue-300/80 bg-white/70 text-blue-900 hover:bg-white'
              }`}
              aria-label={t('library.knowYourself.lockedInsight.unlockAria')}
            >
              {t('library.knowYourself.lockedInsight.unlock')}
            </button>
          </div>

          <p
            className={`mx-auto mt-10 max-w-sm text-center text-sm leading-relaxed ${
              isDark ? 'text-white/85' : 'text-gray-700'
            }`}
          >
            {t('library.knowYourself.lockedInsight.socialProof')}
          </p>

          <section
            className="mt-5"
            aria-label={t('library.knowYourself.lockedInsight.testimonialsCarousel')}
          >
            <div className="hidden flex-col gap-3 motion-reduce:flex">
              {TESTIMONIAL_KEYS.map((key) => (
                <TestimonialCard key={key} tKey={key} t={t} isDark={isDark} />
              ))}
            </div>

            <div className="motion-reduce:hidden">
              <TestimonialMarquee t={t} isDark={isDark} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LockedInsightDetail;
